import type { ResumeTableauDeBord } from '@/api/tableauxDeBord'
import type { Conteneur, Vue } from '@/api/vues'

/**
 * ARBORESCENCE de la barre latérale — module PUR.
 *
 * Une PAGE est ce qu'on ouvre : une vue, ou un tableau de bord (0061) — au
 * même rang. Une entrée de la barre est une page racine (`parentId` nul) ou
 * un CONTENEUR ; les pages rangées dans un conteneur sont ses onglets, vues
 * et tableaux de bord mêlés. La base garantit une seule couche (migrations
 * 0042 et 0061) : un conteneur ne contient que des pages, une page n'en
 * contient aucune.
 */

export type Page = { genre: 'vue'; vue: Vue } | { genre: 'tableau_de_bord'; tableauDeBord: ResumeTableauDeBord }

/** Désignation d'une page : son genre et son identifiant (deux tables, deux espaces d'identifiants). */
export interface RefPage {
  genre: Page['genre']
  id: string
}

export type EntreeArborescence = Page | { genre: 'conteneur'; conteneur: Conteneur; onglets: Page[] }

export const idPage = (p: Page): string => (p.genre === 'vue' ? p.vue.id : p.tableauDeBord.id)
const champsPage = (p: Page) => (p.genre === 'vue' ? p.vue : p.tableauDeBord)

/** Clé UNIQUE d'une page, toutes tables confondues (onglets, clés de rendu). */
export const clePage = (p: RefPage): string => `${p.genre}:${p.id}`
export const refPage = (p: Page): RefPage => ({ genre: p.genre, id: idPage(p) })

/**
 * Pages de l'organisation, dans l'ordre des positions. Chaque liste arrive
 * triée par le serveur ; leur fusion est un tri STABLE par position — à
 * position égale, les vues d'abord, puis l'ordre de lecture du serveur.
 */
export function pagesDe(vues: readonly Vue[], tableauxDeBord: readonly ResumeTableauDeBord[]): Page[] {
  const pages: Page[] = [
    ...vues.map((vue): Page => ({ genre: 'vue', vue })),
    ...tableauxDeBord.map((tableauDeBord): Page => ({ genre: 'tableau_de_bord', tableauDeBord })),
  ]
  return pages.sort((a, b) => champsPage(a).position - champsPage(b).position)
}

/** Onglets d'un conteneur, dans l'ordre de leur `position`. */
export function ongletsDe(pages: readonly Page[], conteneurId: string): Page[] {
  return pages.filter((p) => champsPage(p).parentId === conteneurId)
}

/** La page désignée, ou `null` (pas encore reçue, ou hors des droits). */
export function trouverPage(pages: readonly Page[], ref: RefPage): Page | null {
  return pages.find((p) => p.genre === ref.genre && idPage(p) === ref.id) ?? null
}

/** Le conteneur d'une page, ou `null` (page racine, ou conteneur non reçu). */
export function conteneurDe(pages: readonly Page[], conteneurs: readonly Conteneur[], ref: RefPage): Conteneur | null {
  const page = trouverPage(pages, ref)
  const parentId = page === null ? null : champsPage(page).parentId
  if (parentId === null) return null
  return conteneurs.find((c) => c.id === parentId) ?? null
}

/**
 * Entrées de la barre latérale, dans l'ordre des positions.
 *
 *   * un conteneur SANS onglet visible n'apparaît pas : il n'ouvrirait rien ;
 *   * une page dont le conteneur est absent de la liste reste ATTEIGNABLE, à
 *     la racine — les listes sont lues par plusieurs requêtes, et un
 *     conteneur créé entre deux ne doit pas faire disparaître une page qu'on voit.
 */
export function arborescence(pages: readonly Page[], conteneurs: readonly Conteneur[]): EntreeArborescence[] {
  const connus = new Set(conteneurs.map((c) => c.id))
  const entrees: Array<{ position: number; entree: EntreeArborescence }> = []
  for (const conteneur of conteneurs) {
    const onglets = ongletsDe(pages, conteneur.id)
    if (onglets.length > 0) entrees.push({ position: conteneur.position, entree: { genre: 'conteneur', conteneur, onglets } })
  }
  for (const page of pages) {
    const { parentId, position } = champsPage(page)
    if (parentId === null || !connus.has(parentId)) entrees.push({ position, entree: page })
  }
  // Tri STABLE : à position égale, l'ordre de lecture du serveur est conservé.
  return entrees.sort((a, b) => a.position - b.position).map((e) => e.entree)
}
